/**
 * Task Form Validation Utilities
 * Contains all validation logic for task creation and editing
 */

export const validateTaskForm = (form) => {
  const errors = {};
  const isLandscape = (form.taskCategory || "snowRemoval") === "landscape";

  // Required field validations
  if (!form.employeeOrTeam || (Array.isArray(form.employeeOrTeam) && form.employeeOrTeam.length === 0)) {
    errors.employeeOrTeam = "Please select at least one worker";
  }

  if (!form.taskType) {
    errors.taskType = "Please select task type";
  }

  if (!form.title?.trim()) {
    errors.title = "Task title is required";
  }

  if (!form.priority) {
    errors.priority = "Please select priority";
  }

  if (!form.startDate) {
    errors.startDate = "Please select task start date";
  }

  if (!form.endDate) {
    errors.endDate = "Please select task end date";
  }

  // Date validation
  if (form.startDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(form.startDate);
    startDate.setHours(0, 0, 0, 0);

    if (startDate < today) {
      errors.startDate = "Start date cannot be in the past";
    }
  }

  // End date validation
  if (form.endDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(form.endDate);
    endDate.setHours(0, 0, 0, 0);

    if (endDate < today) {
      errors.endDate = "End date cannot be in the past";
    }
  }

  // Date range validation
  if (form.startDate && form.endDate) {
    const startDate = new Date(form.startDate);
    const endDate = new Date(form.endDate);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (startDate > endDate) {
      errors.endDate = "End date must be on or after start date";
    }
  }

  // Description length validation
  if (form.description && form.description.length > 1000) {
    errors.description = "Description should not exceed 1000 characters";
  }

  // Title length validation
  if (form.title && form.title.length > 100) {
    errors.title = "Title should not exceed 100 characters";
  }

  if (isLandscape) {
    const rows = Array.isArray(form.materialOption) ? form.materialOption : [];
    const filledRows = rows.filter(
      (row) =>
        String(row?.type || "").trim() ||
        String(row?.amount || "").trim() ||
        String(row?.note || "").trim()
    );

    if (filledRows.length === 0) {
      errors.materialOption = "At least one material option is required";
    } else {
      const incompleteRow = filledRows.find(
        (row) =>
          !String(row?.type || "").trim() ||
          !String(row?.amount || "").trim() ||
          !String(row?.note || "").trim()
      );

      if (incompleteRow) {
        errors.materialOption =
          "Each material row must have type, amount, and note";
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validateFileUpload = (file) => {
  const errors = {};

  // File type validation
  const allowedTypes = [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];

  if (!allowedTypes.includes(file.type)) {
    errors.type = "Only Excel files (.xls, .xlsx) are allowed";
  }

  // File size validation (max 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes
  if (file.size > maxSize) {
    errors.size = "File size should be less than 10MB";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const getValidationMessage = (field, errors) => {
  return errors[field] || "";
};

export const hasValidationError = (field, errors) => {
  return !!errors[field];
};
